import { useLazyQuery, useQuery } from "@apollo/client/react"; import React, { useState, useMemo, useEffect } from "react";

import { Box, CircularProgress, Grid } from "@mui/material";
import { adaptProvinciasArray, GET_PROVINCIAS } from "../../../../data/graphQL/queries/Instituciones/getProvincias";
import { GeoPosicionProfesional } from "../components/TabDatosContacto/GeoPosicionProfesional";
import { RenderInputByTypeMobile } from "../../../../components/formGroupGenerator/RenderInputByTypeMobile";
import { adaptLocalidadesArray, GET_LOCALIDADES } from "../../../../data/graphQL/queries/Profesionales/getLocalidades";

export const TabDatosContacto = ({
  allowedWrite,
  isNew,
  profesionalLoading,
  datosContactoFormData,
  setDatosContactoFormData
}) => {

  const [provinciaOptions, setProvinciaOptions] = useState([]);
  const [localidadesOptions, setLocalidadesOptions] = useState([]);

  const validatedPosition = useMemo(() => {
    return !!datosContactoFormData?.latitud && !!datosContactoFormData?.longitud;
  }, [datosContactoFormData?.latitud, datosContactoFormData?.longitud]);

  const handleChange = (name, value) => {
    if (['idProvincia', 'idLocalidad', 'calle', 'numero'].includes(name)) {
      setDatosContactoFormData((prevData) => ({
        ...prevData,
        [name]: value,
        ['latitud']: null,
        ['longitud']: null
      }));
    } else {
      setDatosContactoFormData((prevData) => ({
        ...prevData,
        [name]: value
      }));
    }
  };

  const { data: provincias, loading: provinciasLoading } = useQuery(GET_PROVINCIAS);

  useEffect(() => {
    if (!provincias) return;
    setProvinciaOptions(adaptProvinciasArray(provincias?.provincias.valorEsperado));
  }, [provincias]);

  const [getLocalidades, { data: localidades, loading: localidadesLoading }] = useLazyQuery(GET_LOCALIDADES);

  useEffect(() => {
    if (!localidades) return;
    setLocalidadesOptions(adaptLocalidadesArray(localidades?.localidades.valorEsperado));
  }, [localidades]);

  const loadingPage = useMemo(() => {
    return profesionalLoading || provinciasLoading || localidadesLoading;
  }, [provinciasLoading, profesionalLoading, localidadesLoading]);

  useEffect(() => {
    if (!!datosContactoFormData?.idProvincia) {
      getLocalidades({
        variables: {
          idProvincia: datosContactoFormData?.idProvincia
        }
      });
    }
  }, [datosContactoFormData?.idProvincia]);

  const updatedFields = useMemo(() => {
    return structuredClone(fields).
      map((input) => {
        const updatedInput = { ...input };
        switch (input.name) {
          case 'idProvincia':
            updatedInput.options = provinciaOptions;
            break;
          case 'idLocalidad':
            updatedInput.options = localidadesOptions;
            updatedInput.disabled = !datosContactoFormData?.idProvincia;
            break;
          default:
            break;
        }
        if (!allowedWrite) {
          updatedInput.disabled = true;
        }
        return updatedInput;
      });
  }, [
    provinciaOptions,
    localidadesOptions,
    datosContactoFormData]
  );

  if (loadingPage) return <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '20vh'
    }}>

    <CircularProgress size={40} />
  </Box>;

  return (
    <div className='generic-tab-container'>
      <Grid container spacing={3}>
        {updatedFields.
          map((input, index) =>
            <Grid key={`input-${input.name}-index-${index}`} size={{ md: input.col, xs: input.type !== 'item' ? 12 : 0 }}>
              {input.name !== 'geoPosicionamiento' &&
                <RenderInputByTypeMobile
                  key={`input-${input.name}-index-${index}`}
                  input={input}
                  handleChange={handleChange}
                  value={datosContactoFormData[input.name]} />
              }
              {
                input.name === 'geoPosicionamiento' && <GeoPosicionProfesional
                  datosDireccion={{
                    ...datosContactoFormData,
                    direccion: (datosContactoFormData?.calle || '') + ' ' + (datosContactoFormData?.numero || ''),
                    localidad: localidadesOptions.find((x) => x.id === datosContactoFormData?.idLocalidad)?.displayName,
                    provincia: provinciaOptions.find((x) => x.id === datosContactoFormData?.idProvincia)?.displayName
                  }}
                  handleChange={handleChange}
                  validatedPosition={validatedPosition}
                  onlyView />

              }
            </Grid>
          )
        }
      </Grid>
    </div>);

};

const fields = [
  {
    displayName: 'Calle',
    name: 'calle',
    type: 'string',
    disabled: false,
    col: 3
  },
  {
    displayName: 'Número',
    name: 'numero',
    type: 'string',
    disabled: false,
    col: 2
  },
  {
    displayName: 'Piso',
    name: 'piso',
    type: 'string',
    disabled: false,
    col: 1.5
  },
  {
    displayName: 'Depto',
    name: 'unidad',
    type: 'string',
    disabled: false,
    col: 1.5
  },
  {
    displayName: 'geoPosicionamiento',
    name: 'geoPosicionamiento',
    type: 'geoPosicionamiento',
    col: 1.5
  },
  {
    name: 'item',
    type: 'item',
    col: 2.5
  },
  {
    displayName: 'Provincia',
    name: 'idProvincia',
    type: 'select',
    disabled: false,
    col: 3
  },
  {
    displayName: 'Buscador de localidad',
    name: 'idLocalidad',
    type: 'autocomplete',
    disabled: false,
    col: 3
  },
  {
    displayName: 'C.Postal',
    name: 'codigoPostal',
    type: 'string',
    disabled: false,
    col: 2
  },
  {
    name: 'item',
    type: 'item',
    col: 4
  },
  {
    displayName: 'Observaciones',
    name: 'observacion',
    type: 'long_string',
    disabled: false,
    col: 12
  },
  {
    displayName: 'Ej. +54 (Cod. área) número de teléfono',
    name: 'texttext',
    type: 'text',
    col: 12
  },
  {
    displayName: 'Tel. Particular',
    name: 'telefonoParticular',
    type: 'tel',
    disabled: false,
    col: 3
  },
  {
    displayName: 'Tel. Movil',
    name: 'telefonoMovil',
    type: 'tel',
    disabled: false,
    col: 3
  },
  {
    displayName: 'Tel. Laboral',
    name: 'telefonoLaboral',
    type: 'tel',
    disabled: false,
    col: 3
  },
  {
    name: 'item',
    type: 'item',
    col: 3
  }];